import { defineStore } from 'pinia';
import type { InstallmentState, InstallmentSimulatedPlan } from '@/types/Installment';
import notificationWrapper from '@/utils/helpers/notificationWrapper';
import { useConfigStore } from './configStore';
import { useProjectStore } from './projectStore';
import type { ErrorResponse } from '@/types/global';

const DEVICE_TYPES = {
  IPhone: "IPhone",
  Ipad: "Ipad",
  AndroidSmartPhone: "AndroidSmartPhone",
  AndroidTablet: "AndroidTablet",
  SmartPhone: "SmartPhone",
  Tablet: "Tablet",
  Desktop: "Desktop",
} as const;

type DeviceType = keyof typeof DEVICE_TYPES;

const getDeviceType = (ua: string): DeviceType => {
  if (/iPhone/i.test(ua)) return "IPhone";
  if (/iPad/i.test(ua)) return "Ipad";
  if (/Android/i.test(ua)) {
    if (/Mobile/i.test(ua)) return "AndroidSmartPhone";
    return "AndroidTablet";
  }
  if (/Mobile|Phone/i.test(ua)) return "SmartPhone";
  if (/Tablet/i.test(ua)) return "Tablet";
  return "Desktop";
}

export const useInstallmentStore = defineStore('installment', {
  state: (): InstallmentState => ({
    installmentPaymentLoading: false,
    installmentPaymentError: null,
    installmentLoading: false,
    installmentSimulatedPlanLoading: false,
    installmentSimulatedPlans: [],
    deviceType: getDeviceType(navigator.userAgent),
    linkInstallmentPayment: null,
    errorMessage: '',
  }),

  actions: {
    async getSimulateInstallmentPayment({ token, quoteId, amount }: { token: string, quoteId?: string, amount?: number }) {
      this.installmentSimulatedPlanLoading = true;
      return window.appConnector.getSimulateInstallmentPayment(token, quoteId, amount)
        .then((response: {
          simulatedInstallmentPlans: InstallmentSimulatedPlan[];
        }) => {
          this.installmentSimulatedPlans = response.simulatedInstallmentPlans;
        })
        .catch((error: any) => {
          notificationWrapper();
          throw error;
        })
        .finally(() => {
          this.installmentSimulatedPlanLoading = false;
        });
        ;
    },
    async postInstallmentPayment({ token, quoteId }: { token: string, quoteId: string }) {
      this.installmentLoading = true;
      const configStore = useConfigStore();
      configStore.setStepLoading(true);
      return window.appConnector.postInstallmentPayment(token, {
        quote_id: quoteId,
        device: this.deviceType,
      })
        .then((response: {
          links: {
            href: string;
        }[] }) => {
          this.linkInstallmentPayment = response.links?.[0];
        })
        .catch((error: ErrorResponse) => {
          notificationWrapper();
          throw error;
        })
        .finally(() => {
          this.installmentLoading = false;
          configStore.setStepLoading(false);
        });
    },
    async postInstallmentPaymentPrebookCheck(quoteId: string) {
      this.installmentLoading = true;
      const projectStore = useProjectStore();
      return window.appConnector.postInstallmentPaymentPrebookCheck(projectStore.projectIdSelected ?? '', quoteId)
        .finally(() => {
          this.installmentLoading = false;
        });
    },
  },
});
